import { MILLISECONDS_PER_DAY } from '@/utils/date'
import { formatNegativeParens } from '@/utils/format'

export const ANNOTATION_BORDER_WIDTH = 1.5
export const STEP_SIZES_ALLOWED = [
    0.1, 0.25, 0.5, 1, 2.5, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000, 250000, 500000,
    1000000, 2500000, 5000000, 10000000, 25000000, 50000000, 100000000, 250000000, 500000000, 1000000000, 2500000000,
    5000000000,
]

export function hLine(value, axis) {
    return {
        id: `h-line-${value}`,
        type: 'line',
        mode: 'horizontal',
        scaleID: axis,
        value: value,
        borderColor: 'rgba(151, 151, 151,1)',
        borderWidth: ANNOTATION_BORDER_WIDTH,
        borderDash: [2, 2],
    }
}

export function vLine(value, axis) {
    return {
        id: `v-line-${value}`, // optional
        type: 'line',
        mode: 'vertical',
        scaleID: axis,
        value: value,
        borderColor: 'rgba(151, 151, 151,1)',
        borderWidth: ANNOTATION_BORDER_WIDTH,
        borderDash: [2, 2],
    }
}

export function box(xMin, xMax, yMin, yMax, xAxis, yAxis) {
    return {
        type: 'box',
        id: `box-${xMin}-${xMax}-${yMin}-${yMax}`,
        xScaleID: xAxis,
        yScaleID: yAxis,
        xMin: xMin,
        xMax: xMax,
        yMax: yMax,
        yMin: yMin,
        borderColor: 'transparent',
        borderWidth: ANNOTATION_BORDER_WIDTH,
        borderDash: [5, 5],
        borderDashOffset: 5,
        backgroundColor: 'rgba(201, 201, 201, 0.1)',
    }
}

export function stepSize(range) {
    var fiveStepSize = range / 5
    return STEP_SIZES_ALLOWED.reduceRight((acc, curr) => {
        if (curr > fiveStepSize) {
            return curr
        }
        return acc
    }, fiveStepSize)
}

export function trimData(array, spacing) {
    const trimmed = []
    const end = array.length - 1
    for (var index = 0; index < end; index++) {
        if (index % spacing == 0) {
            trimmed.push(array[index])
        }
    }
    trimmed.push(array[array.length - 1])
    return trimmed
}

export function timeUnit(min, max) {
    const days = (max - min) / MILLISECONDS_PER_DAY

    if (days >= 540) {
        return 'year'
    }

    if (days >= 30) {
        return 'month'
    }

    return 'day'
}

export function timeAxis(min, max) {
    return {
        type: 'time',
        time: {
            unit: timeUnit(min, max),
            displayFormats: {
                year: 'YYYY',
                month: 'MMM YYYY',
                day: 'MMM D, YYYY',
            },
        },
        ticks: {
            display: true,
            maxTicksLimit: 5,
            min: min,
            max: max,
        },
        id: 'x-axis',
        gridLines: {
            display: false,
        },
    }
}

export function computeRangeAndStep(min, max) {
    const step = stepSize(max - min)
    min = Math.floor(min / step) * step
    max = Math.ceil(max / step) * step

    return {
        min,
        max,
        step,
    }
}

export function linearAxis(min, max) {
    const stepper = stepSize(max - min)
    min = Math.floor(min / stepper) * stepper
    max = Math.ceil(max / stepper) * stepper

    return {
        id: 'y-axis',
        weight: 100,
        position: 'right',
        type: 'linear',
        ticks: {
            display: true,
            min: min,
            max: max,
            stepSize: stepper,
            callback: formatNegativeParens,
        },
        scaleLabel: {
            display: true,
        },
        gridLines: {
            display: true,
        },
    }
}

export function snapToStep(value) {
    return STEP_SIZES_ALLOWED.find(v => v > value) ?? value
}

function defaultPadding() {
    return {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    }
}

export function computePadding(input) {
    if (input === null || input === undefined) {
        return defaultPadding()
    }

    if (typeof input === 'string') {
        input = input.split(' ').filter(n => n)
    }

    if (typeof input === 'number') {
        return {
            top: input,
            right: input,
            bottom: input,
            left: input,
        }
    }

    if (Array.isArray(input)) {
        switch (input.length) {
            case 0:
                return defaultPadding()
            case 1:
                return computePadding(parseInt(input[0]) || 0)
            case 2:
                return {
                    top: parseInt(input[0]) || 0,
                    right: parseInt(input[1]) || 0,
                    bottom: parseInt(input[0]) || 0,
                    left: parseInt(input[1]) || 0,
                }
            case 3:
                return {
                    top: parseInt(input[0]) || 0,
                    right: parseInt(input[1]) || 0,
                    bottom: parseInt(input[2]) || 0,
                    left: parseInt(input[1]) || 0,
                }
            default:
                return {
                    top: parseInt(input[0]) || 0,
                    right: parseInt(input[1]) || 0,
                    bottom: parseInt(input[2]) || 0,
                    left: parseInt(input[3]) || 0,
                }
        }
    }

    return {
        ...input,
        ...defaultPadding(),
    }
}

export const pieColors = [
    '#05DBF2',
    '#1497A6',
    '#0762F9',
    '#078EDB',
    '#06EA87',
    '#DBEA06',
    '#EA8706',
    '#EA3B06',
    '#8706EA',
    '#EA06DB',
]
